/**
 * First-run journey:
 *  language → welcome → how it works (+consent) → due date → names
 *  → try tapping the 3D bump (feel the value BEFORE being asked to sign up)
 *  → account (email code / Google) → hospital phone → home (+ coach marks)
 */
import { config } from '../../data/config';
import { repo } from '../../data/repo';
import { track } from '../../data/track';
import { pregnancyInfo, toLocalISODate, validateDueDate, DAY_MS } from '../../domain/pregnancy';
import type { Locale, Profile } from '../../domain/types';
import { LANGS, setLocale, t } from '../../i18n';
import { sfx } from '../../audio/sfx';
import { $, esc, icons, toast, vibrate } from '../dom';
import { go, register, type Screen } from '../router';
import { mountBelly, unmountBelly } from '../bellyHost';
import { afterSignInRoute } from '../flow';

// ------------------------------------------------------------ draft (survives the Google redirect)
interface Draft { dueDate: string; displayName: string; babyNickname: string; consented: boolean }
const DRAFT_KEY = 'bumpkick.draft';
function loadDraft(): Draft {
  try { const r = sessionStorage.getItem(DRAFT_KEY); if (r) return JSON.parse(r) as Draft; } catch { /* ignore */ }
  return { dueDate: '', displayName: '', babyNickname: '', consented: false };
}
const draft = loadDraft();
function saveDraft() { try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch { /* ignore */ } }
export function hasPendingDraft(): boolean { return draft.consented && !!draft.dueDate && !!draft.displayName; }

/** Turn the onboarding draft into the real profile (after sign-in in cloud mode). */
export function commitDraft(): void {
  const profile: Profile = {
    displayName: draft.displayName.trim(),
    babyNickname: draft.babyNickname.trim() || null,
    dueDate: draft.dueDate,
    locale: repo.locale ?? 'th',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Bangkok',
    hospitalPhone: null,
    soundOn: true,
    reminderEnabled: false,
    reminderTime: '20:00'
  };
  repo.saveProfile(profile);
  repo.recordConsents(['privacy_policy', 'health_data', 'not_medical_device']);
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

const progress = (step: number, of = 5) =>
  `<div class="ob-progress">${Array.from({ length: of }, (_, i) => `<span class="${i < step ? 'on' : ''}"></span>`).join('')}</div>`;
const top = (step: number) => `<div class="ob-top"><button class="back-btn" data-action="back" aria-label="${esc(t('common', 'back'))}">${icons.back}</button>${progress(step)}<span style="width:40px"></span></div>`;

// ------------------------------------------------------------ language
const lang: Screen = {
  id: 'lang', root: true,
  render: () => `
    <div class="spacer"></div>
    <div style="text-align:center;margin-bottom:28px">
      <div style="font-size:44px">🌸</div>
      <h2>${esc(t('lang', 'title'))}</h2>
      <p class="muted">${esc(t('lang', 'subtitle'))}</p>
    </div>
    <div class="lang-list" style="margin:0 auto">
      ${LANGS.map(l => `<button class="lang-btn" data-action="pick-lang" data-lang="${l.code}" ${l.available ? '' : 'disabled'}>
        <span>${esc(l.label)}</span>${l.available ? '<span>›</span>' : `<small>${esc(t('lang', 'soon'))}</small>`}</button>`).join('')}
    </div>
    <div class="spacer"></div>`,
  actions: {
    'pick-lang': el => {
      const l = el.dataset.lang as Locale;
      setLocale(l); repo.setLocale(l); sfx.tap();
      go('welcome');
    }
  }
};

// ------------------------------------------------------------ welcome
const welcome: Screen = {
  id: 'welcome',
  render: () => `
    <div class="ob-top"><button class="back-btn" data-action="back" aria-label="${esc(t('common', 'back'))}">${icons.back}</button><span></span><span style="width:40px"></span></div>
    <div class="spacer"></div>
    <div style="text-align:center">
      <div class="welcome-belly" aria-hidden="true" style="margin:0 auto 24px">
        <div class="blob"></div><div class="navel"></div>
        <span class="heart" style="right:0;top:18px">💗</span>
        <span class="heart" style="left:4px;bottom:30px;animation-delay:1.2s;font-size:16px">✨</span>
      </div>
      <h1>${esc(t('welcome', 'title'))}</h1>
      ${t('welcome', 'titleLocal') ? `<div class="title-local">${esc(t('welcome', 'titleLocal'))}</div>` : ''}
      <p class="welcome-copy">${esc(t('welcome', 'lead'))}</p>
    </div>
    <div class="spacer"></div>
    <div class="stack screen-pad-bottom">
      <button class="btn btn-primary btn-block" data-action="go" data-to="how">${esc(t('welcome', 'start'))}</button>
      ${config.cloud ? `<button class="btn btn-ghost btn-block" data-action="go" data-to="login">${esc(t('welcome', 'haveAccount'))}</button>` : ''}
      <p class="small muted" style="text-align:center">${esc(t('welcome', 'trial'))}</p>
    </div>`
};

// ------------------------------------------------------------ how it works + consent
const how: Screen = {
  id: 'how',
  render: () => `
    ${top(1)}
    <div class="ob-body">
      <h2>${esc(t('how', 'title'))}</h2>
      <div class="fact-list">
        <div class="fact"><div class="ico" style="background:var(--rose-soft)">🔟</div><div><b>${esc(t('how', 'p1t'))}</b><p>${esc(t('how', 'p1'))}</p></div></div>
        <div class="fact"><div class="ico" style="background:var(--sage-soft)">👆</div><div><b>${esc(t('how', 'p2t'))}</b><p>${esc(t('how', 'p2'))}</p></div></div>
        <div class="fact"><div class="ico" style="background:var(--amber-soft)">⏱️</div><div><b>${esc(t('how', 'p3t'))}</b><p>${esc(t('how', 'p3'))}</p></div></div>
      </div>
      <p class="small" style="color:var(--fg2)">${esc(t('how', 'note'))}</p>
      <label class="check"><input type="checkbox" id="c-privacy" ${draft.consented ? 'checked' : ''}><span>${t('how', 'consentPrivacy')}</span></label>
      <label class="check"><input type="checkbox" id="c-health" ${draft.consented ? 'checked' : ''}><span>${esc(t('how', 'consentHealth'))}</span></label>
    </div>
    <div class="ob-footer"><button class="btn btn-primary btn-block" id="how-next" data-action="how-next">${esc(t('common', 'next'))}</button></div>`,
  mount(root) {
    const upd = () => { ($('#how-next', root) as HTMLButtonElement).disabled = !(($('#c-privacy', root) as HTMLInputElement).checked && ($('#c-health', root) as HTMLInputElement).checked); };
    root.addEventListener('change', upd); upd();
  },
  actions: { 'how-next': () => { draft.consented = true; saveDraft(); go('due'); } }
};

// ------------------------------------------------------------ due date
const due: Screen = {
  id: 'due',
  render: () => {
    const min = toLocalISODate(new Date(Date.now() - 14 * DAY_MS));
    const max = toLocalISODate(new Date(Date.now() + 280 * DAY_MS));
    return `
    ${top(2)}
    <div class="ob-body">
      <h2>${esc(t('due', 'title'))}</h2>
      <p class="muted">${esc(t('due', 'lead'))}</p>
      <div class="field">
        <label for="due-input">${esc(t('due', 'label'))}</label>
        <input type="date" id="due-input" min="${min}" max="${max}" value="${esc(draft.dueDate)}">
        <span class="error" id="due-error" hidden></span>
      </div>
      <div class="week-preview" id="week-preview" hidden></div>
    </div>
    <div class="ob-footer"><button class="btn btn-primary btn-block" id="due-next" data-action="due-next" disabled>${esc(t('common', 'next'))}</button></div>`;
  },
  mount(root) {
    const input = $('#due-input', root) as HTMLInputElement;
    const check = () => {
      const v = input.value;
      const err = validateDueDate(v);
      const errEl = $('#due-error', root)!, prev = $('#week-preview', root)!;
      ($('#due-next', root) as HTMLButtonElement).disabled = !!err;
      errEl.hidden = !(err && err !== 'empty');
      errEl.textContent = err === 'too_far' ? t('due', 'errTooFar') : err === 'too_past' ? t('due', 'errTooPast') : '';
      prev.hidden = !!err;
      if (!err) {
        const p = pregnancyInfo(v);
        prev.innerHTML = `<div class="eyebrow" style="color:var(--rose-dark)">${esc(t('due', 'nowAt'))}</div>
          <div class="big num">${p.week}</div>
          <div style="font-weight:600;color:var(--rose-dark)">${esc(t('due', 'weeksDays', { d: p.day }))}</div>
          <div class="small muted" style="margin-top:4px">${esc(p.daysLeft >= 0 ? t('due', 'daysLeft', { n: p.daysLeft }) : t('due', 'pastDue', { n: -p.daysLeft }))}</div>
          ${p.week < 28 ? `<div class="small muted" style="margin-top:6px">${esc(t('due', 'early'))}</div>` : ''}`;
      }
    };
    input.addEventListener('input', check); input.addEventListener('change', check); check();
  },
  actions: { 'due-next': () => { draft.dueDate = ($('#due-input') as HTMLInputElement).value; saveDraft(); go('names'); } }
};

// ------------------------------------------------------------ names
const names: Screen = {
  id: 'names',
  render: () => `
    ${top(3)}
    <div class="ob-body">
      <h2>${esc(t('names', 'title'))}</h2>
      <div class="field"><label for="mom-input">${esc(t('names', 'mom'))}</label>
        <input type="text" id="mom-input" maxlength="40" autocomplete="given-name" placeholder="${esc(t('names', 'momPh'))}" value="${esc(draft.displayName)}"></div>
      <div class="field"><label for="baby-input">${esc(t('names', 'baby'))}</label>
        <input type="text" id="baby-input" maxlength="30" placeholder="${esc(t('names', 'babyPh'))}" value="${esc(draft.babyNickname)}"></div>
    </div>
    <div class="ob-footer"><button class="btn btn-primary btn-block" id="names-next" data-action="names-next">${esc(t('common', 'next'))}</button></div>`,
  mount(root) {
    const mom = $('#mom-input', root) as HTMLInputElement;
    const upd = () => { ($('#names-next', root) as HTMLButtonElement).disabled = !mom.value.trim(); };
    mom.addEventListener('input', upd); upd();
  },
  actions: {
    'names-next': () => {
      draft.displayName = ($('#mom-input') as HTMLInputElement).value.trim();
      draft.babyNickname = ($('#baby-input') as HTMLInputElement).value.trim();
      saveDraft();
      // Came from the instant start screen: she has already tapped, so skip the practice tap.
      if (!repo.user && repo.kicks.length) { commitDraft(); go('hospital'); } else go('tryit');
    }
  }
};

// ------------------------------------------------------------ try the bump (practice, not saved)
const tryit: Screen = {
  id: 'tryit',
  render: () => `
    ${top(4)}
    <div style="text-align:center;padding-top:8px">
      <h2 id="try-title">${esc(t('tryit', 'title'))}</h2>
      <p class="muted" id="try-lead">${esc(t('tryit', 'lead'))}</p>
    </div>
    <div class="stage-wrap" style="flex:1;display:flex;flex-direction:column">
      <div class="belly-stage" id="try-stage"></div>
      <div class="stage-hint" id="try-hint">${esc(t('tryit', 'hint'))}</div>
    </div>
    <div class="ob-footer"><button class="btn btn-primary btn-block" id="try-next" data-action="try-next" disabled>${esc(t('common', 'next'))}</button></div>`,
  mount(root) {
    let n = 0;
    void mountBelly($('#try-stage', root)!, () => {
      n++; sfx.kick(n); vibrate(20);
      if (n === 1) {
        $('#try-title', root)!.textContent = t('tryit', 'great');
        $('#try-lead', root)!.textContent = t('tryit', 'greatLead');
        $('#try-hint', root)!.hidden = true;
        ($('#try-next', root) as HTMLButtonElement).disabled = false;
      }
    }, { spatial: true });
  },
  unmount: unmountBelly,
  // No account needed to start: she tries it first, everything stays on this device,
  // and she makes an account later from the profile, the home nudge or when buying.
  actions: { 'try-next': () => { if (!repo.user) commitDraft(); go('hospital'); } }
};

// ------------------------------------------------------------ account (email code / Google)
function authScreen(id: 'account' | 'login'): Screen {
  let email = '';
  let codeSent = false;
  const isLogin = id === 'login';
  return {
    id,
    render: () => {
      if (!config.cloud) {
        return `${top(5)}<div class="ob-body"><h2>${esc(t('account', 'demoTitle'))}</h2><p class="muted">${esc(t('account', 'demoLead'))}</p></div>
          <div class="ob-footer"><button class="btn btn-primary btn-block" data-action="demo-continue">${esc(t('account', 'demoContinue'))}</button></div>`;
      }
      return `
      ${isLogin || repo.profile ? `<div class="ob-top"><button class="back-btn" data-action="back">${icons.back}</button><span></span><span style="width:40px"></span></div>` : top(5)}
      <div class="ob-body">
        <h2>${esc(t('account', isLogin ? 'loginTitle' : 'title'))}</h2>
        <p class="muted">${esc(t('account', isLogin ? 'loginLead' : 'lead'))}</p>
        <div id="auth-step"></div>
      </div>`;
    },
    mount(root) { if (config.cloud) renderStep(root); },
    actions: {
      'demo-continue': () => { commitDraft(); go('hospital'); },
      google: async () => {
        try { saveDraft(); await repo.signInWithGoogle(); } catch { toast(t('common', 'error')); }
      },
      'send-code': async (el) => {
        const input = $('#email-input') as HTMLInputElement;
        email = input.value.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast(t('account', 'badEmail')); return; }
        (el as HTMLButtonElement).disabled = true;
        try { await repo.sendEmailCode(email); codeSent = true; renderStep(el.closest('section')!); }
        catch { toast(t('common', 'error')); (el as HTMLButtonElement).disabled = false; }
      },
      'verify-code': async (el) => {
        const code = ($('#code-input') as HTMLInputElement).value.replace(/\D/g, '');
        if (code.length !== 6) { toast(t('account', 'badCode')); return; }
        (el as HTMLButtonElement).disabled = true;
        try {
          await repo.verifyEmailCode(email, code);
          track.once('account_linked'); track.event('auth_verified');
          afterSignIn();
        } catch { toast(t('account', 'badCode')); (el as HTMLButtonElement).disabled = false; }
      },
      'change-email': (el) => { codeSent = false; renderStep(el.closest('section')!); }
    }
  };

  function renderStep(root: HTMLElement) {
    const box = $('#auth-step', root)!;
    box.innerHTML = !codeSent ? `
      <div class="stack" style="margin-top:8px">
        ${config.googleEnabled ? `<button class="btn btn-google btn-block" data-action="google">${icons.google}${esc(t('account', 'google'))}</button>
        <div class="divider">${esc(t('account', 'or'))}</div>` : ''}
        <div class="field"><label for="email-input">${esc(t('account', 'email'))}</label>
          <input type="email" id="email-input" autocomplete="email" inputmode="email" placeholder="${esc(t('account', 'emailPh'))}" value="${esc(email)}"></div>
        <button class="btn btn-primary btn-block" data-action="send-code">${esc(t('account', 'sendCode'))}</button>
      </div>` : `
      <div class="stack" style="margin-top:8px">
        <p class="small" style="color:var(--fg2)">${esc(t('account', 'codeSent', { email }))}</p>
        <div class="field"><label for="code-input">${esc(t('account', 'code'))}</label>
          <input id="code-input" class="code-input" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="••••••"></div>
        <button class="btn btn-primary btn-block" data-action="verify-code">${esc(t('account', 'verify'))}</button>
        <div class="row" style="justify-content:space-between">
          <button class="link-btn" data-action="change-email">${esc(t('account', 'changeEmail'))}</button>
          <button class="link-btn" data-action="send-code-again">${esc(t('account', 'resend'))}</button>
        </div>
      </div>`;
    const resend = box.querySelector('[data-action="send-code-again"]');
    resend?.addEventListener('click', () => void repo.sendEmailCode(email).then(() => toast(t('common', 'saved'))).catch(() => toast(t('common', 'error'))));
    (box.querySelector('input') as HTMLInputElement | null)?.focus();
  }
}

function clearDraft(): void {
  Object.assign(draft, { dueDate: '', displayName: '', babyNickname: '', consented: false });
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

/** After any sign-in. An existing account's profile is never overwritten by a new draft (see ui/flow.ts). */
export function afterSignIn(): void {
  const r = afterSignInRoute(hasPendingDraft(), !!repo.profile);
  if (r.to === 'home') {
    if (r.discardDraft) { clearDraft(); toast(t('account', 'welcomeBack')); }
    else toast(t('account', 'savedToAccount'));
    go('home', { replace: true });
  } else if (r.to === 'hospital') {
    commitDraft(); go('hospital', { replace: true });
  } else {
    go('how', { replace: true });
  }
}

// ------------------------------------------------------------ hospital phone
const hospital: Screen = {
  id: 'hospital',
  render: () => `
    <div class="ob-top"><span style="width:40px"></span>${progress(5)}<span style="width:40px"></span></div>
    <div class="ob-body">
      <div style="font-size:40px">🏥</div>
      <h2>${esc(t('hospital', 'title'))}</h2>
      <p class="muted">${esc(t('hospital', 'lead'))}</p>
      <div class="field"><label for="hosp-input">${esc(t('hospital', 'label'))}</label>
        <input type="tel" id="hosp-input" inputmode="tel" autocomplete="off" maxlength="20" placeholder="${esc(t('hospital', 'ph'))}" value="${esc(repo.profile?.hospitalPhone ?? '')}">
        <span class="error" id="hosp-err" hidden>${esc(t('hospital', 'invalid'))}</span></div>
    </div>
    <div class="ob-footer"><button class="btn btn-primary btn-block" data-action="hosp-finish">${esc(t('hospital', 'finish'))}</button></div>`,
  root: true,
  actions: {
    'hosp-finish': () => {
      const v = ($('#hosp-input') as HTMLInputElement).value.trim();
      if (v && !/^[0-9+() -]{3,20}$/.test(v)) { $('#hosp-err')!.hidden = false; return; }
      const p = repo.profile;
      if (p) repo.saveProfile({ ...p, hospitalPhone: v || null });
      track.once('onboarding_done');
      go('home', { replace: true });
    }
  }
};

register(lang, welcome, how, due, names, tryit, authScreen('account'), authScreen('login'), hospital);
